<template>
    <ValidationObserver
        ref="fixItRecipient"
        v-slot="{ passes }"
    >
        <BaseDialog
            :showDialog="showDialog"
            headline="lang_GENERIC_CAPTION_FIX_IT"
            :close="closeDialog"
            maxDialogWidth="900"
            maxCardHeight="600"
            :alvqa="alvqa"
        >
            <template v-slot:dialogText>
                <v-container fluid>
                    <v-row>
                        <!-- Current Recipient ID (read-only) -->
                        <v-col cols="8">
                            <v-text-field
                                :value="currentRecipientDisplay"
                                :label="$t('lang_PUBLISHING_INCOME_CAPTION_FIXIT_CURRENT_RECIPIENT')"
                                readonly
                                disabled
                            ></v-text-field>
                        </v-col>

                        <!-- Source (read-only) -->
                        <v-col cols="4">
                            <v-text-field
                                :value="sourceName"
                                :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                                readonly
                                disabled
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <!-- A-Z Group and Person Autocomplete -->
                            <BaseEditPageAutocomplete 
                                :context="context"
                                field="NewRecipient"
                                :label="$t('lang_PUBLISHING_INCOME_CAPTION_FIXIT_NEW_RECIPIENT')"
                                :withMargin="false"
                                rules="required"
                                :additionalFunction="recipientSelected"
                                :alvqa="alvqa + '-autocomplete-recipient-textfield'"
                            >
                                <template v-slot:selection="{ item }">
                                    <div class="ellipsis" :alvqa="alvqa + '-autocomplete-recipient-selection'">
                                        {{ 
                                            typeof item === 'object' ? 
                                                $alvPlugin.showNameOfAzEntry(item) : item 
                                        }}
                                    </div>
                                </template>
                                <template v-slot:items="{ items }">
                                    <v-list-item-content :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId">
                                        <v-list-item-title :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-title'">
                                            {{ $alvPlugin.showNameOfAzEntry(items) }}
                                            <v-avatar color="grey" size="20" class="caption">{{ items.typeOfAz }}</v-avatar>
                                        </v-list-item-title>
                                        <v-list-item-subtitle :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-subtitle'">
                                            {{ $t('lang_IPI') }}: {{ items.ipi !== '' ? items.ipi : $t('lang_GENERIC_CAPTION_NONE') }} | {{ items.alvId }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </BaseEditPageAutocomplete>
                        </v-col>
                    </v-row>
                </v-container>
            </template>

            <!-- APPLY button -->
            <template v-slot:dialogActions>
                <v-container fluid>
                    <v-row>
                        <v-spacer />
                        <v-col class="text-right">
                            <BaseButton 
                                :disabled="disableApplyButton"
                                label="lang_GENERIC_CAPTION_APPLY"
                                @click="passes(apply)"
                                :alvqa="alvqa+'-button-apply'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'

export default {
    name: "FixItRecipientDialog",
    props: {
        incomeFile: {
            type: Object,
            required: true
        }
    },
    components: {
        BaseDialog,
        BaseButton,
        BaseEditPageAutocomplete,
    },
    data() {
        return {
            context: "FixItRecipient",
            alvqa: 'publishing-income-list-dialog-fix-it-recipient',
            disableApplyButton: true,
            currentRecipientName: '',
            currentRecipientAlvIdCache: null,
        }
    },
    computed: {
        showDialog: {
            get() { return this.$store.getters.getShowFixItRecipientDialog },
            set(show) { this.$store.commit('setShowFixItRecipientDialog', show) }
        },
        errorItem() {
            return this.$store.getters.getFixItRecipientErrorObject
        },
        currentRecipientId() {
            return this.errorItem && this.errorItem.recipientId 
                ? this.errorItem.recipientId 
                : '';
        },
        currentRecipientDisplay() {
            if (!this.currentRecipientName) {
                return this.currentRecipientId;
            }
            
            let display = this.currentRecipientName;
            let details = [];
            
            if (this.currentRecipientId) {
                details.push(this.currentRecipientId);
            }
            
            if (this.currentRecipientAlvIdCache) {
                details.push(this.currentRecipientAlvIdCache);
            }
            
            if (details.length > 0) {
                display += ' (' + details.join(' | ') + ')';
            }
            
            return display;
        },
        sourceName() {
            return this.incomeFile && this.incomeFile.source
                ? this.incomeFile.source
                : '';
        },
        sourceAlvId() {
            return this.incomeFile && this.incomeFile.sourceAlvId
                ? this.incomeFile.sourceAlvId
                : '';
        },
        incomeFileAlvId() {
            return this.incomeFile && this.incomeFile.alvId 
                ? this.incomeFile.alvId 
                : '';
        },
        recipientSelected() {
            const selectedRecipient = this.$store.getters.getFixItRecipientDetailsNewRecipient;
            if (selectedRecipient == null)
                this.disableApplyButton = true;
            else
                this.disableApplyButton = false;
        },
    },
    watch: {
        async showDialog(isOpen) {
            if (isOpen && this.currentRecipientId) {
                // Fetch current recipient details when dialog opens
                await this.fetchCurrentRecipientDetails();
            }
        }
    },
    methods: {
        async fetchCurrentRecipientDetails() {
            if (!this.currentRecipientId) return;
            
            try {
                const azResponse = await this.$store.dispatch('searchAzForFixItRecipient', this.currentRecipientId);
                if (azResponse && azResponse.length > 0) {
                    const recipient = azResponse[0];
                    this.currentRecipientName = this.$alvPlugin.showNameOfAzEntry(recipient);
                    this.currentRecipientAlvIdCache = recipient.alvId;
                }
            } catch (error) {
                console.error('Error fetching current recipient details:', error);
                this.currentRecipientName = '';
                this.currentRecipientAlvIdCache = null;
            }
        },
        closeDialog() {
            this.showDialog = false;
            this.disableApplyButton = true;
            // Reset cached recipient info
            this.currentRecipientName = '';
            this.currentRecipientAlvIdCache = null;
            // Clear all Vuex store values
            this.$store.commit('setFixItRecipientDetailsNewRecipient', null);
            this.$store.commit('setFixItRecipientNewRecipientOptions', []);
            this.$store.commit('setFixItRecipientErrorObject', null);
        },
        async apply() {
            this.disableApplyButton = true;
            const selectedRecipient = this.$store.getters.getFixItRecipientDetailsNewRecipient;
            
            if (selectedRecipient && selectedRecipient.alvId) {
                this.$store.commit('setIsLoading', true);
                
                // Search for Work ALVID using workIdentifier
                let workAlvId = null;
                if (this.errorItem.workIdentifier) {
                    try {
                        const worksResponse = await this.$store.dispatch('searchWorksForFixItRecipient', this.errorItem.workIdentifier);
                        if (worksResponse && worksResponse.length > 0) {
                            workAlvId = worksResponse[0].alvId;
                        }
                    } catch (error) {
                        console.error('Error searching for work:', error);
                    }
                }
                
                // Use cached Current Recipient ALVID (already fetched when dialog opened)
                let currentRecipientAlvId = this.currentRecipientAlvIdCache;
                
                const payload = {
                    incomeFileAlvId: this.incomeFileAlvId,
                    sourceAlvId: this.sourceAlvId,
                    currentRecipientAlvId: currentRecipientAlvId,
                    newRecipientAlvId: selectedRecipient.alvId,
                    // Include error item metadata for backend to identify the exact row
                    workIdentifier: this.errorItem.workIdentifier,
                    title: this.errorItem.title,
                    workAlvId: workAlvId,
                    incomeErrorListRowAlvId: this.errorItem.alvId || this.errorItem.rowAlvId || null,
                    // Statement IDs for database update
                    incomeRowIds: this.errorItem.incomeRowIds || [],
                };
                
                this.$store.commit('setIsLoading', false);
                this.$store.dispatch('fixItRecipient', payload);
                this.closeDialog();
            }
        },
    },
}
</script>





